import { CommonModule, registerLocaleData } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClient } from '@angular/common/http';
import { NgModule } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { AccessDeniedInterceptor } from '@core/interceptors/access-denied.interceptor';
import { ToastrGuardService } from './core/services/toastr-guard.service';
import { APP_INITIALIZER } from '@angular/core';
import { MiniGamesModule } from '@games/games.module';
import { StoreModule } from '@ngrx/store';
import {
  MissingTranslationHandler,
  TranslateCompiler,
  TranslateLoader,
  TranslateModule,
} from '@ngx-translate/core';
import { provideCharts, withDefaultRegisterables } from 'ng2-charts';
import { provideEnvironmentNgxMask } from 'ngx-mask';
import { ToastrModule } from 'ngx-toastr';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { CoreModule } from './core/core.module';
import MultiTranslateHttpLoaderFactory from './core/multi-translate-loader-library';
import { ModalsModule } from './modals/modals.module';
import { SpinnerOverlayModule } from './new-core/archived/components/spinner-overlay/spinner-overlay.module';
import { NewCoreModule } from './new-core/new-core.module';
import { GamesModule } from './pages/games/games.module';
import { MgtModule } from './pages/mgt/mgt.module';
import { ALL_GAMES } from '@core/services';
import { provideGames } from '@games/core/operator';
import localeRo from '@angular/common/locales/ro';
import localeKz from '@angular/common/locales/kk';
import localePl from '@angular/common/locales/pl';
import localeUa from '@angular/common/locales/uk';
import localeUz from '@angular/common/locales/uz';
import localeKa from '@angular/common/locales/ka';
import { provideMethodicMaterials } from '@games/core/lib/methodic-materials/filters';
import { CalculatorModule } from '@shared/components/games/calculator/calculator.module';
import { CalculatorLayoutComponent } from '@shared/components/games/input-layouts/calculator-layout/calculator-layout.component';
import { VirtualKeyboardDirective } from '@games/core/input/VirtualKeyboard';
import { SnowAnimationModule } from '@shared/components/snow-animation/snow-animation.module';
import { TranslateCompilerWithUaMapping } from '@core/translate-compiler-with-ua-mapping';
import { FallbackChainMissingTranslationHandler } from '@core/i18n';

const locales = [
  { data: localeRo, code: 'ro' },
  { data: localeKz, code: 'kz' },
  { data: localePl, code: 'pl' },
  { data: localeUa, code: 'ua' },
  { data: localeUz, code: 'uz' },
  { data: localeKa, code: 'ge' },
];

locales.forEach((loc) => registerLocaleData(loc.data, loc.code));

const dutchRangeLabel = (page: number, pageSize: number, length: number) => {
  if (length == 0 || pageSize == 0) {
    return `0 из ${length}`;
  }

  length = Math.max(length, 0);

  const startIndex = page * pageSize;

  // If the start index exceeds the list length, do not try and fix the end index to the end.
  const endIndex =
    startIndex < length ? Math.min(startIndex + pageSize, length) : startIndex + pageSize;

  return `${startIndex + 1} - ${endIndex} из ${length}`;
};

export function getPaginatorIntl() {
  const paginatorIntl = new MatPaginatorIntl();

  paginatorIntl.firstPageLabel = 'Первая страница';
  paginatorIntl.lastPageLabel = 'Последняя страница';
  paginatorIntl.itemsPerPageLabel = 'Показывать по';
  paginatorIntl.nextPageLabel = 'Следующая страница';
  paginatorIntl.previousPageLabel = 'Предыдущая страница';
  paginatorIntl.getRangeLabel = dutchRangeLabel;

  return paginatorIntl;
}

@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserModule,
    AppRoutingModule,
    GamesModule,
    MiniGamesModule,
    CoreModule,
    NewCoreModule,
    CommonModule,
    BrowserAnimationsModule,
    ModalsModule,
    FormsModule,
    MgtModule,
    MatCheckboxModule,
    MatButtonModule,
    MatProgressBarModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: MultiTranslateHttpLoaderFactory,
        deps: [HttpClient],
      },
      compiler: {
        provide: TranslateCompiler,
        useClass: TranslateCompilerWithUaMapping,
      },
      missingTranslationHandler: {
        provide: MissingTranslationHandler,
        useClass: FallbackChainMissingTranslationHandler,
      },
      useDefaultLang: true,
    }),
    ToastrModule.forRoot({
      progressBar: true,
      closeButton: true,
      progressAnimation: 'increasing',
      timeOut: 6000,
      extendedTimeOut: 4000,
      tapToDismiss: false,
    }),
    StoreModule.forRoot({}, {}),
    SpinnerOverlayModule,
    CalculatorModule,
    CalculatorLayoutComponent,
    VirtualKeyboardDirective,
    SnowAnimationModule,
  ],
  bootstrap: [AppComponent],
  providers: [
    provideGames(ALL_GAMES),
    // provideMethodicMaterials(),

    provideEnvironmentNgxMask(),
    provideCharts(withDefaultRegisterables()),
    { provide: MatPaginatorIntl, useValue: getPaginatorIntl() },
    ToastrGuardService,
    {
      provide: APP_INITIALIZER,
      useFactory: (toastrGuard: ToastrGuardService) => () => {
        // Принудительно инициализируем ToastrGuardService
        return Promise.resolve();
      },
      deps: [ToastrGuardService],
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AccessDeniedInterceptor,
      multi: true,
    },
    //TODO временно отключен из-за неправильной работы на играх
    /*    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpLoadingInterceptor,
      multi: true,
    },*/
  ],
})
export class AppModule {}
